import './App.css';
import { Container, Row, Col, Button, Modal, Image } from 'react-bootstrap';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import React from 'react';
import TopNav from './components/NavBar';
import SignupRedirect from './pages/SignupRedirect';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import GettingStarted from './pages/GettingStarted';
import InviteAccept from './pages/InviteAccept';
import RequireAuth from './auth/RequireAuth';
import AuthCallback from './pages/AuthCallback';
import Pricing from './pages/Pricing';
import RoiCalculator from './pages/RoiCalculator';
import ContactRedirect from './pages/ContactRedirect';
import ForgotPassword from './pages/ForgotPassword';
import UpdatePassword from './pages/UpdatePassword';
import ResendEmail from './pages/ResendEmail';
import PasswordReset from './pages/PasswordReset';
import HelpChatIcon from './components/HelpChatIcon';
import NoSites from './pages/NoSites';
import { getTrafficBucket, applyInternalParamFromUrl, isInternalAnalyticsEnabled } from './lib/internalAnalytics';
import { trackGaEvent } from './lib/ga';
const SuperAdmin = React.lazy(() => import('./pages/SuperAdmin'));

function Home() {
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = React.useState(false);
  const supabaseBase = process.env.REACT_APP_SKINNY_SUPABASE_URL || '';
  const videoSrc = supabaseBase
    ? `${supabaseBase}/storage/v1/object/public/assets/IntroSkinny.mp4`
    : '/assets/IntroSkinny.mp4';
  function openChatWithSeed() {
    try {
      sessionStorage.setItem('SKINNY_SEED', JSON.stringify({ text: 'Why use TheSkinnyAI?', submit: true, ts: Date.now(), scope: 'site' }));
    } catch (_) {}
    try {
      const host = document.querySelector('[data-skinny-button][data-skinny-scope="site"]') || document.querySelector('[data-skinny-button]');
      const btn = host && host.shadowRoot && host.shadowRoot.querySelector('.btn');
      if (btn) btn.click();
    } catch (_) {}
  }
  return (
    <main>
      <section className="py-5 bg-light border-bottom">
        <Container>
          <Row className="align-items-center">
            <Col md={7} className="mb-4 mb-md-0">
              <h1 className="display-5 fw-bold">
              Turn Every Visitor Into a Qualified Lead — Instantly with TheSkinnyAI
                <HelpChatIcon className="ms-2" title="Learn why teams add TheSkinnyAI to their sites" seedQuestion="Why use TheSkinnyAI?" />
              </h1>
              <div className="mt-3">
                <Button variant="outline-secondary" onClick={() => setShowVideo(true)} aria-label="Play Intro Video">
                  <Image src="/TheSkinnyTVBlue.png" alt="Play video" height={36} />
                  <span className="ms-2 align-middle">Watch Intro</span>
                </Button>
              </div>
              <p className="lead text-muted mt-3">
                You've poured hours into SEO-optimized content to draw visitors in—but once they're there, do they find what they need? TheSkinnyAI adds a seamless chat add-on to your site, letting users qualify your products or services instantly: "Does your product fit my needs?" Setup is trivial: just one line of JavaScript. Tune it to your brand, and watch customers self-qualify—saving time, boosting conversions, and reducing friction without overhauling your site. Sign up, submit your URL, and test before going live.
              </p>
              <div className="mt-4 d-flex justify-content-center gap-2 flex-wrap">
                <Button variant="primary" className="d-inline-flex flex-column align-items-center" onClick={() => navigate('/signup')}>
                  <span>Get Started Free</span>
                  <small className="fw-normal" style={{ fontSize: '.75rem', lineHeight: 1, opacity: .9 }}>(No Credit Card Required)</small>
                </Button>
                <Button variant="outline-secondary" className="d-inline-flex flex-column align-items-center" onClick={openChatWithSeed}>
                  <span>Try it</span>
                  <small className="fw-normal" style={{ fontSize: '.75rem', lineHeight: 1, opacity: .9 }}>(Test TheSkinnyAI)</small>
                </Button>
                <Button
                  variant="outline-secondary"
                  className="d-inline-flex flex-column align-items-center"
                  onClick={() => navigate('/contact?message='+encodeURIComponent("I'd like a free demonstration of TheSkinnyAI."))}
                >
                  <span>Request a Demo</span>
                  <small className="fw-normal" style={{ fontSize: '.75rem', lineHeight: 1, opacity: .9 }}>(With Your Site Data)</small>
                </Button>
              </div>
            </Col>
            <Col md={5}>
              <div className="p-4 bg-white rounded shadow-sm">
                <h2 className="h5 mb-3">Unlock the Magic</h2>
                <ul className="mb-0">
                <li>Pure Website Intelligence</li>
                <li>Powerful Insights and Analysis</li>
                <li>Responses that adapt like a clever assistant</li>
                  <li>Effortlessly harvest your site's knowledge</li>
                  <li>Concierge onboarding and setup</li>
                  <li>Lightning fast and works anywhere</li>
                  <li>Automatic language detection</li>
                  <li>Includes 5 user seats</li>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <Modal show={showVideo} onHide={() => setShowVideo(false)} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>Intro to TheSkinnyAI</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ width: '100%', maxWidth: 480, margin: '0 auto' }}>
            <div style={{ aspectRatio: '480 / 854' }}>
              <video controls playsInline src={videoSrc} style={{ width: '100%', height: '100%' }} />
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </main>
  );
}

function useGoogleAnalytics() {
  const location = useLocation();
  React.useEffect(() => {
    if (process.env.REACT_APP_ENABLE_ANALYTICS !== '1') return;
    trackGaEvent('_init', { page_path: location.pathname + location.search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    const debugGA = process.env.REACT_APP_GA_DEBUG === '1';
    if (process.env.REACT_APP_ENABLE_ANALYTICS !== '1') {
      if (debugGA) console.log('[GA] analytics disabled (REACT_APP_ENABLE_ANALYTICS !== 1)');
      return;
    }
    (async () => {
      try {
        const path = location.pathname + location.search;
        const ua = (navigator && navigator.userAgent) ? navigator.userAgent : '';
        const isHidden = typeof document !== 'undefined' && document.visibilityState && document.visibilityState !== 'visible';
        const isBot =
          (typeof navigator !== 'undefined' && navigator.webdriver === true) ||
          /\b(bot|crawl|spider|headless|slackbot|whatsapp|facebookexternalhit|ahrefs|googleother|preview|link|expander|prerender|phantom)\b/i.test(ua);
        const requiresAuth = /^\/(getting-started|dashboard|account)\b/.test(location.pathname);
        // Always check session to set auth_state user property
        let hasSession = false;
        try {
          const { supabase } = await import('./lib/supabaseClient');
          const sess = await supabase.auth.getSession();
          hasSession = !!(sess && sess.data && sess.data.session);
        } catch (_) {
          hasSession = false;
        }
        const isInternal = isInternalAnalyticsEnabled();
        if (debugGA) console.log('[GA] guards:', { path, isHidden, isBot, requiresAuth, hasSession, isInternal });
        // Skip GA entirely if internal mode is enabled
        if (isInternal) {
          if (debugGA) console.log('[GA] page_view skipped - internal mode enabled');
          return;
        }
        if (!isHidden && !isBot && (!requiresAuth || hasSession)) {
          if (debugGA) console.log('[GA] sending page_view', { path, title: document.title });
          // Use one call so it won't be lost if gtag loads late.
          trackGaEvent('page_view', { page_path: path, page_title: document.title });
          // Best-effort user properties (non-critical).
          try {
            if (typeof window.gtag === 'function') {
              window.gtag('set', 'user_properties', {
                traffic_bucket: getTrafficBucket(),
                auth_state: hasSession ? 'authenticated' : 'anonymous',
              });
            }
          } catch (_) { /* ignore */ }
        } else {
          if (debugGA) console.log('[GA] page_view skipped due to guards');
        }
      } catch (e) {
        if (debugGA) console.log('[GA] error:', e);
      }
    })();
  }, [location.pathname, location.search]);
}

function useAhrefsPageviews() {
  const location = useLocation();
  React.useEffect(() => {
    try {
      if (typeof window.ahrefsTrack === 'function') {
        window.ahrefsTrack('pageview', { path: location.pathname + location.search });
      } else if (window && window.dispatchEvent) {
        window.dispatchEvent(new Event('ahrefs:pageview'));
      }
    } catch (_) { /* ignore */ }
  }, [location.pathname, location.search]);
}

function App() {
  // Apply ?internal=1|0 URL param once on mount (before analytics)
  React.useEffect(() => { applyInternalParamFromUrl(); }, []);
  useGoogleAnalytics();
  useAhrefsPageviews();
  React.useEffect(() => {
    const tenantId = process.env.REACT_APP_SKINNY_SKINNYID;
    const widgetUrl = process.env.REACT_APP_SKINNY_WIDGET_URL || `${window.location.origin}/chatbot-widget.js`;
    const apiEndpoint = process.env.REACT_APP_SKINNY_PUBLIC_API_ENDPOINT;
    const apiKey = process.env.REACT_APP_SKINNY_PUBLIC_API_KEY;
    const anonKey = process.env.REACT_APP_SKINNY_SUPABASE_ANON_KEY; // for unauthenticated widget calls
    if (!tenantId) return;
    // Avoid duplicate injection
    if (document.querySelector('script[data-skinny-global]')) return;
    const s = document.createElement('script');
    s.src = widgetUrl;
    s.dataset.tenantId = tenantId;
    s.dataset.name = 'Ask Skinny';
    s.dataset.scope = 'site';
    // Responsive position: default mobile BR; tablet BL; desktop TL
    let pos = 'bottom-right';
    try {
      const isDesktop = window.matchMedia('(min-width: 1200px)').matches;
      const isTablet = window.matchMedia('(min-width: 576px) and (max-width: 1199.98px)').matches;
      if (isTablet) pos = 'bottom-left';
      else if (isDesktop) pos = 'bottom-right';
    } catch (_) { /* ignore */ }
    s.dataset.position = pos;
    try {
      const cfg = (typeof window !== 'undefined' && window.__SKINNY_SITE_CONFIG) || {};
      s.dataset.theme = cfg.widgetTheme || 'light';
    } catch (_) {
      s.dataset.theme = 'light';
    }
    if (apiEndpoint) s.dataset.apiEndpoint = apiEndpoint;
    if (apiKey) s.dataset.apiKey = apiKey;
    if (anonKey) s.dataset.anonKey = anonKey;
    s.setAttribute('data-skinny-global', '1');
    document.body.appendChild(s);
    return () => { try { s.remove(); } catch (_) {} };
  }, []);

  return (
    <>
      <TopNav />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/index.html" element={<Navigate to="/" replace />} />
        <Route path="/try-it" element={<Navigate to="/" replace />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/roi-calculator" element={<RoiCalculator />} />
        <Route path="/contact" element={<ContactRedirect />} />
        <Route path="/signup" element={<SignupRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/resendemail" element={<ResendEmail />} />
        <Route path="/password-reset" element={<PasswordReset />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route element={<RequireAuth />}>
          <Route path="/account" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/getting-started" element={<GettingStarted />} />
          <Route path="/no-sites" element={<NoSites />} />
        </Route>
        <Route path="/invite/accept" element={<InviteAccept />} />
        <Route path="/superadmin" element={
          <React.Suspense fallback={<div className="p-4 text-muted">Loading…</div>}>
            <SuperAdmin />
          </React.Suspense>
        } />
      </Routes>
    </>
  );
}

export default App;
